import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from '../Modal'
import Boton from '../Boton'
import { avisosApi } from '../../api/avisos'
import { fmtFechaHora } from '../../utils/fecha'
import { colors, text, radius, sx } from '../../design/tokens'

/**
 * Panel de la campanita: los avisos que el usuario tiene ABIERTOS (leídos pero
 * no resueltos — los sin leer los maneja la ventana emergente). Cada uno
 * navega a su documento y se marca "✓ Hecho" cuando se cumplió lo pedido.
 *
 * `soloLectura` (nombre del imitado): el super admin está "viendo como" ese
 * usuario. Ve sus avisos —también los sin leer, que en ese modo no salen como
 * emergente— pero no los marca: el acuse es de la persona (el backend lo
 * rechaza igual con 403).
 */
export default function PanelAvisos({ avisos, onResuelto, onClose, soloLectura = null }) {
  const navigate = useNavigate()
  const [resolviendo, setResolviendo] = useState(null)

  const resolver = async (aviso) => {
    setResolviendo(aviso.id)
    try {
      await avisosApi.resolver(aviso.id)
      onResuelto(aviso.id)
    } catch {
      window.alert('No se pudo marcar el aviso. Probá de nuevo.')
    } finally {
      setResolviendo(null)
    }
  }

  return (
    <Modal title={soloLectura ? `🔔 Avisos de ${soloLectura}` : '🔔 Mis avisos'} onClose={onClose} maxWidth={560}>
      {soloLectura && avisos.length > 0 && (
        <p style={st.notaImitando}>
          👁 Estás viendo como {soloLectura}. Sus avisos se muestran para mirar: leerlos o darlos por hechos es de esa persona.
        </p>
      )}
      {avisos.length === 0 ? (
        <p style={{ color: colors.textSec, fontSize: text.body, margin: 0 }}>
          No tenés avisos pendientes. Cuando alguien te deje uno, va a aparecer acá
          (y como ventana emergente hasta que lo marques leído).
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {avisos.map(a => (
            <div key={a.id} style={st.item}>
              <div style={st.itemHead}>
                <span style={st.de}>{a.de?.nombre}</span>
                <span style={st.fecha}>{fmtFechaHora(a.created_at)}</span>
              </div>
              {a.referencia && (
                <button
                  style={st.refLink}
                  onClick={() => { onClose(); navigate(a.referencia.ruta) }}
                >
                  {a.referencia.etiqueta} →
                </button>
              )}
              <div style={st.mensaje}>“{a.mensaje}”</div>
              {soloLectura ? (
                !a.leido_at && <div style={st.sinLeer}>Sin leer todavía</div>
              ) : (
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <Boton variante="success" sm disabled={resolviendo === a.id} onClick={() => resolver(a)}>
                    {resolviendo === a.id ? 'Marcando…' : '✓ Hecho'}
                  </Boton>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}

const st = {
  item: {
    border: `1px solid ${colors.border}`, borderRadius: radius.sm,
    padding: '10px 12px', background: colors.bgSoft,
  },
  itemHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 },
  de: { fontWeight: 600, fontSize: text.body, color: colors.text },
  fecha: { fontSize: text.meta, color: colors.textMeta },
  refLink: {
    marginTop: 4, padding: 0, border: 'none', background: 'none', cursor: 'pointer',
    color: colors.link, fontSize: text.sec, fontWeight: 600, ...sx.mono,
  },
  mensaje: { marginTop: 6, fontSize: text.body, color: colors.text, whiteSpace: 'pre-wrap', lineHeight: 1.5 },
  sinLeer: { marginTop: 8, fontSize: text.sec, color: colors.warn, fontWeight: 600, textAlign: 'right' },
  notaImitando: {
    margin: '0 0 12px', padding: '8px 12px', borderRadius: radius.sm,
    background: colors.warnBg, color: colors.text, fontSize: text.sec, lineHeight: 1.4,
  },
}
